/**
 * SmoothScroller — §8 / HERO-SCROLL-GUIDE v1 "Lenis bridge".
 *
 * Rules implemented (these are law, see guide):
 *  1. ONE heartbeat: Lenis is driven from gsap.ticker — never its own rAF loop.
 *  2. lenis.on('scroll', ScrollTrigger.update) keeps pinned triggers in sync.
 *  3. gsap.ticker.lagSmoothing(0) so a busy main thread never desyncs scrub.
 *  4. CDN failure is non-fatal: children render either way, native scroll remains.
 */
import { useEffect, type ReactNode } from 'react';
import { loadGsap, loadLenis, type LenisInstance } from '../lib/cdn';

export default function SmoothScroller({ children }: { children: ReactNode }) {
  useEffect(() => {
    let lenis: LenisInstance | null = null;
    let tickerFn: ((time: number) => void) | null = null;
    let cancelled = false;
    // Native one-finger scrolling owns touch-capable viewports. Besides feeling more
    // direct, this prevents a stopped smooth-scroll instance from capturing touch
    // input if a mobile browser reports an unexpected primary-pointer type.
    const touchCapable = navigator.maxTouchPoints > 0
      || window.matchMedia('(pointer: coarse)').matches;

    (async () => {
      try {
        if (touchCapable) return;
        const { gsap, ScrollTrigger } = await loadGsap();
        const Lenis = await loadLenis();
        if (cancelled || !Lenis) return;

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return; // native scroll only — ScrollTrigger still works unsmoothed

        lenis = new Lenis({
          lerp: 0.12,
          smoothWheel: true,
          wheelMultiplier: 1,
          touchMultiplier: 1.8,
          // Lock fine-pointer input to the vertical axis (these equal the 1.1.13
          // defaults). Touch-capable viewports never instantiate this controller.
          orientation: 'vertical',
          gestureOrientation: 'vertical',
        });

        lenis.on('scroll', ScrollTrigger.update);

        tickerFn = (time: number) => {
          lenis?.raf(time * 1000); // gsap ticker is seconds; Lenis wants ms
        };
        gsap.ticker.add(tickerFn);
        gsap.ticker.lagSmoothing(0);
      } catch {
        /* CDN unreachable → graceful: plain native scrolling */
      }
    })();

    // The hero has no scroll lock (owner decision 2026-07-18 — Apple pattern):
    // Lenis just lerps; it is never stopped or steered by the hero.
    return () => {
      cancelled = true;
      if (tickerFn && window.gsap) window.gsap.ticker.remove(tickerFn);
      lenis?.destroy();
    };
  }, []);

  return <>{children}</>;
}
